import React, { useState, useRef, useEffect } from 'react';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import classnames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import styles from './styles';
import UploadIcon from 'assets/images/upload-icon.svg';
import { withStyles } from '@material-ui/core/styles';
import ImageCard from './components/ImageCard/ImageCard';
import { v4 as uuidv4 } from 'uuid';
import { useArrayHistoryChanges } from 'components/WorklogDetails/hooks/useArrayHistoryChanges';
import { useGetHistoryItem } from 'components/WorklogDetails/hooks/useGetHistoryItem';
import { useLocation, useNavigate } from 'react-router-dom';
import { getProjectName } from 'helpers/_helpers';
import { PhotoViewer } from 'lib';

const imageUrl = (key) => `${process.env.REACT_APP_BASE_URL}/sheets/image?key=${key}`;

const toViewerImage = ({ _id, key, x480 }, caption) => ({
  id: _id,
  src: imageUrl(key),
  previewSrc: x480 && imageUrl(x480),
  caption,
});

const ImagesBlock = ({ classes, hasAccess, sheet, historyState }) => {
  const fileInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [picturesToSend, setPicturesToSend] = useState([]);
  const [randomKey, setRandomKey] = useState(Math.random().toString(36));
  const [viewer, setViewer] = useState(null);
  const isViewerOpen = !!location.state?.photoViewer;

  // a reload or remount keeps the history flag but loses the snapshot
  useEffect(() => {
    if (isViewerOpen && !viewer) navigate(location, { replace: true });
  }, [isViewerOpen, viewer, location, navigate]);

  const handleCustomButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const selectedFiles = await Array.from(event.target.files);

    const uploadedImageUrls = selectedFiles.map((file) => ({
      img: `${URL.createObjectURL(file)}`,
      size: file.size,
      file: file,
      localId: uuidv4(),
    }));
    setPicturesToSend([...picturesToSend, ...uploadedImageUrls]);
    setRandomKey(Math.random().toString(36));
  };

  const { removedItems, addedItems, changedItems } = useArrayHistoryChanges(historyState?.diff?.images);
  const getHistoryItem = useGetHistoryItem(sheet.images, addedItems, changedItems);

  const pendingPictures = picturesToSend.filter(
    (sendImage) => (sheet?.images || []).every((sheetImage) => sendImage.localId !== sheetImage._id)
  );

  const openViewer = (id) => {
    const images = [
      ...(sheet?.images || []).map((image) =>
        toViewerImage(image, getHistoryItem(image).action === 'added' ? 'Added' : undefined)
      ),
      ...removedItems.map((image) => toViewerImage(image, 'Removed')),
      ...pendingPictures.map(({ localId, img }) => ({ id: localId, src: img })),
    ];
    setViewer({ images, index: images.findIndex((image) => image.id === id) });
    navigate(location, { state: { photoViewer: true } });
  };

  return (
    <>
      {hasAccess && <Grid container alignItems="center" xs={12} md={12}>
        <IconButton
          className={classes.btn}
          disableRipple
          disableTouchRipple
          onClick={handleCustomButtonClick}
        >
          <img src={UploadIcon} alt="upload" />
        </IconButton>
        <input
          type="file"
          accept="image/*"
          multiple
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          key={randomKey}
        />
        <Typography
          fontWeight={'fontWeightBold'}
          color="primary"
          className={classnames(classes.bold, classes.uploadBtn)}
          onClick={handleCustomButtonClick}
        >
          Upload
        </Typography>
      </Grid>}
      <Grid container xs={12} md={12}>
        {sheet?.images?.map((image, i) => (
          <Grid item key={image?.img || image._id} className={classes.cardWrapper}>
            <ImageCard
              data={image}
              index={i}
              sheet={sheet}
              setPicturesToSend={setPicturesToSend}
              picturesToSend={picturesToSend}
              hasAccess={hasAccess}
              historyItem={getHistoryItem(image)}
              onOpen={() => openViewer(image._id)}
            />
          </Grid>
        ))}
        {removedItems?.map((image, i) => (
          <Grid item key={image?.img || image._id} className={classes.cardWrapper}>
            <ImageCard
              data={image}
              index={i}
              sheet={sheet}
              setPicturesToSend={setPicturesToSend}
              picturesToSend={picturesToSend}
              hasAccess={hasAccess}
              removed={true}
              onOpen={() => openViewer(image._id)}
            />
          </Grid>
        ))}
        {pendingPictures.map((image, i) => (
          <Grid item key={image?.img || image._id} className={classes.cardWrapper}>
            <ImageCard
              data={image}
              index={i}
              sheet={sheet}
              setPicturesToSend={setPicturesToSend}
              picturesToSend={picturesToSend}
              hasAccess={hasAccess}
              onOpen={() => openViewer(image.localId)}
            />
          </Grid>
        ))}
      </Grid>
      {viewer && (
        <PhotoViewer
          open={isViewerOpen}
          images={viewer.images}
          index={viewer.index}
          title={[getProjectName(sheet.project), sheet.project?.county].filter(Boolean).join(', ')}
          onIndexChange={(index) => setViewer((prev) => ({ ...prev, index }))}
          onClose={() => navigate(-1)}
        />
      )}
    </>
  );
};

export default withStyles(styles)(ImagesBlock);
